import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@material-ui/core";

const STREAM_CONSTRAINTS = { video: { facingMode: "user" } };

/**
 * Dialog reusable para capturar una foto con la webcam del navegador.
 * Cleanup automático del MediaStream al cerrar.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onCapture: (blob: Blob) => void  // recibe la captura como Blob JPEG
 */
const WebcamCaptureDialog = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const detenerStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const iniciarStream = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const stream =
        await navigator.mediaDevices.getUserMedia(STREAM_CONSTRAINTS);
      // Guard contra cierre durante el await: si el componente ya se desmontó, soltar el stream y salir.
      if (!videoRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.addEventListener(
        "loadedmetadata",
        () => setLoading(false),
        { once: true },
      );
    } catch (err) {
      setLoading(false);
      if (err.name === "NotAllowedError") {
        setError(
          "No se pudo acceder a la cámara. Revisa los permisos del navegador y reintenta.",
        );
      } else if (err.name === "NotFoundError") {
        setError("No se detectó una cámara. Verifica que esté conectada.");
      } else if (err.name === "NotReadableError") {
        setError(
          "La cámara está siendo usada por otra aplicación. Ciérrala y reintenta.",
        );
      } else {
        setError(`No se pudo acceder a la cámara: ${err.message || err.name}`);
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      iniciarStream();
    }
    return () => detenerStream();
  }, [open, iniciarStream, detenerStream]);

  const capturar = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(blob);
        detenerStream();
        onClose();
      },
      "image/jpeg",
      0.9,
    );
  }, [onCapture, onClose, detenerStream]);

  const handleCerrar = useCallback(() => {
    detenerStream();
    onClose();
  }, [detenerStream, onClose]);

  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Tomar foto con cámara</DialogTitle>
      <DialogContent>
        {error ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={iniciarStream}>
              Reintentar
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 8 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCerrar}>Cancelar</Button>
        <Button
          onClick={capturar}
          variant="contained"
          disabled={!!error || loading}
        >
          Capturar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WebcamCaptureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCapture: PropTypes.func.isRequired,
};

export default WebcamCaptureDialog;
